import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  Icon,
  TextField,
} from "@mui/material";
import DropdownSelector from "../../../atoms/Common/DropdownSelector";
import LGAutocomplete from "../../../atoms/Common/LGAutocomplete.tsx";
import { getPOItemList } from "../../../api/purchaseOrders";
import { getProductList } from "../../../api/products";
import { getCatalogue } from "../../../api/common/catalogues.ts";
import { FastField, Field, useFormikContext } from "formik";
import { LGNumber } from "../../../atoms/Inputs/LGNumber.tsx";

interface FormValues {
  inbound: {
    companyId: number | null;
    purchaseOrderId: number | null;
    description: string;
    warehouseId: number | null;
  };
  inboundItems: Array<{
    poItemId: number | null;
    description: string;
    deliveryNo: number;
    quantityDelivered: number;
    weight: number;
    productId: number | null;
    productCode?: string;
    unitId: number | null;
    workOrder: string;
  }>;
}

interface InboundItemsProps {
  purchaseOrderId: number | null;
  formMode: string;
  companyId?: number;
}

const InboundItems: React.FC<InboundItemsProps> = ({
  purchaseOrderId,
  formMode,
  companyId,
}) => {
  const [items, setItems] = useState<any[]>([]);
  const { values, setFieldValue, errors, touched } =
    useFormikContext<FormValues>();

  const handleAddItem = () => {
    const newItem = {
      poItemId: null,
      description: "",
      deliveryNo: 1,
      quantityDelivered: 0,
      weight: 0,
      productId: null,
      productCode: "",
      unitId: null,
      workOrder: "",
      orderQuantity: 0,
    };

    setItems((prev) => [...prev, newItem]);

    // Also update the formik values
    const currentItems = values.inboundItems || [];
    setFieldValue("inboundItems", [...currentItems, newItem]);
  };

  const [poItems, setPoItems] = useState<any[]>([]);

  useEffect(() => {
    if (purchaseOrderId) {
      getPOItemList({ purchaseOrderId }).then((response) => {
        setPoItems(response.data);
      });
    }
  }, [purchaseOrderId]);

  useEffect(() => {
    // Sync items with Formik values in edit mode or when items are loaded from PO
    if (formMode === "edit" || (values.inboundItems && values.inboundItems.length > 0)) {
      setItems(values.inboundItems || []);
    }
  }, [formMode, values.inboundItems]);

  const handleRemoveItem = (index: number) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);

    // Update formik values
    setFieldValue("inboundItems", newItems);
  };

  const handleItemChange = (index: number, field: string, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);

    // Update formik values
    setFieldValue(`inboundItems[${index}].${field}`, value);
  };


  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Productos
      </Typography>
      {items.map((item, index) => (
        <Box
          pb={2}
          m={1}
          key={index}
          sx={{ border: "1px solid #aaaaaa", borderRadius: 1, p: 2 }}
        >
          <Grid container spacing={2}>
            <Grid item xs={11}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {item.poItemId ? (
                    <TextField
                      label="Producto"
                      value={item.productCode || ""}
                      disabled
                      variant="outlined"
                      fullWidth
                    />
                  ) : (
                    <LGAutocomplete
                      fetchFunction={getProductList}
                      optionLabel={["code", "description"]}
                      optionSeparator=" -- "
                      queryParams={companyId ? { companyId } : {}}
                      onChange={(event, value) => {
                        setFieldValue(
                          `inboundItems[${index}].productId`,
                          value ? value.id : null
                        );
                      }}
                      label="Producto"
                    />
                  )}
                </Grid>

                <Grid item xs={3}>
                  <FastField
                    name={`inboundItems[${index}].workOrder`}
                    as={TextField}
                    label="Orden de Trabajo"
                    variant="outlined"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={3}>
                  <FastField
                    name={`inboundItems[${index}].unitId`}
                    as={DropdownSelector}
                    label="Unidad"
                    fetchFunction={getCatalogue}
                    fetchParams={{ catalogueName: "UNIT" }}
                    optionLabel="name"
                  />
                </Grid>

                <Grid item xs={3}>
                  <FastField
                    name={`inboundItems[${index}].quantityDelivered`}
                    as={LGNumber}
                    label="Cantidad"
                    type="number"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={3}>
                  <FastField
                    name={`inboundItems[${index}].weight`}
                    as={LGNumber}
                    label="Peso (kg)"
                    type="number"
                    fullWidth
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FastField
                    name={`inboundItems[${index}].description`}
                    as={TextField}
                    label="Descripción"
                    multiline
                    rows={2}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Typography
                  variant="caption"
                  sx={{ pl: 2, color: "text.secondary" }}
                >
                  {`Cantidad entregada/total: ${items[index].quantityDelivered} / ${items[index].orderQuantity}`}
                </Typography>
              </Grid>
            </Grid>
            <Grid item xs={1}>
              <IconButton onClick={() => handleRemoveItem(index)} color="error">
                <Icon>delete</Icon>
              </IconButton>
            </Grid>
          </Grid>
        </Box>
      ))}
      <Button variant="contained" onClick={handleAddItem}>
        Agregar producto
      </Button>
    </Box>
  );
};

export default InboundItems;
